import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, tap, firstValueFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { environment as env } from 'src/environments/environment';
import { SuccessResponse } from 'src/app/common/models/SuccessRespons';
import { LocalStorageService } from 'src/app/common/services/local-storage.service';
import { SessionService } from 'src/app/quote/services/session.service';
import { LoginPayload } from 'src/app/common/models';
import { AppState } from '../../app.reducer';
import * as authActions from '../auth.actions';

@Injectable({
  providedIn: 'root',
})
export class LoginService {
  private BASE_URL = `${env.PROPERTYBR_API_BASE_URL}/security`;

  constructor(
    private httpClient: HttpClient,
    private localStorageService: LocalStorageService,
    private sessionService: SessionService,
    private store: Store<AppState>
  ) {}

  login(data: LoginPayload) {
    const URL = `${this.BASE_URL}/login`;

    return this.httpClient.post<SuccessResponse>(URL, this.getLoginDto(data)).pipe(
      tap(({ result }) => {
        this.preNotifyLoginDatadog(result.token);
        this.localStorageService.set('sessionToken', result.token);
        this.localStorageService.set('sessionExpireIn', result.expireIn);

        const { userInfo, groups = [] } = result;

        this.localStorageService.set('brokerSessionInfo', userInfo);
        this.localStorageService.set('userGroups', groups);

        this.store.dispatch(authActions.setBrokerSessionInfo({ brokerSessionInfo: userInfo }));
        this.store.dispatch(authActions.setUserGroups({ userGroups: groups }));

        this.sessionService.initInactivityTimer();
        this.sessionService.initSessionTimer();
      }),
      map(() => true)
    );
  }

  requestResetPassword(data: any) {
    const URL = `${this.BASE_URL}/restore-password`;
    return this.httpClient.post<SuccessResponse>(URL, this.getRequestResetPassowordDto(data));
  }

  resetPassword(data: any) {
    const URL = `${this.BASE_URL}/change-password`;
    return this.httpClient.put<SuccessResponse>(URL, this.getResetPassowordDto(data));
  }

  private getLoginDto(data: any) {
    return {
      captchaToken: data.captchaToken,
      documentNumber: data.documentNumber,
      code: data.code,
      username: btoa(data.username),
      password: btoa(data.password),
    };
  }

  private getRequestResetPassowordDto(data: any) {
    return {
      organizationUnitName: `BR${data.documentNumber}`,
      username: data.username,
    };
  }

  private getResetPassowordDto(data: any) {
    return {
      token: data.token,
      password: data.password,
    };
  }

  getUserInfo(token: string) {
    const URL = `${this.BASE_URL}/user-info`;
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
    return this.httpClient.get<SuccessResponse>(URL, { headers });
  }

  async preNotifyLoginDatadog(token: any) {
    const { result } = await firstValueFrom(this.getUserInfo(token));
    const { userInfo } = result;
    this.notifyLoginDatadog(userInfo);
  }

  notifyLoginDatadog(userInfo: any): void {
    return (
      (window as any).DD_RUM &&
      (window as any).DD_RUM.setUser({
        id: userInfo.userId,
        name: userInfo.username,
        email: userInfo.email || '',
      })
    );
  }
}
